import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Plus, Users, Edit2, Trash2, Calendar, Sparkles } from "lucide-react";
import { format, parseISO, differenceInYears } from "date-fns";

const CONDITIONS = [
  "Autism Spectrum Disorder",
  "ADHD",
  "Dyslexia",
  "Sensory Processing Disorder",
  "Speech Delay",
  "Developmental Delay",
  "Down Syndrome",
  "Cerebral Palsy",
  "Other"
];

export default function ChildrenPage() {
  const { user, refreshUser } = useAuth();
  const confirm = useConfirmation();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [form, setForm] = useState({
    name: "",
    date_of_birth: "",
    conditions: [],
    notes: ""
  });

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await apiClient.get("/children");
      setChildren(response.data);
    } catch (error) {
      toast.error("Failed to load children");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChild) {
        await apiClient.put(`/children/${editingChild.id}`, form);
        toast.success("Child profile updated!");
      } else {
        await apiClient.post("/children", form);
        toast.success("Child profile created!");
      }
      fetchChildren();
      refreshUser();
      resetForm();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to save";
      if (error.response?.data?.code === 'tier_limit_children') {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete child profile?", description: "The child profile and its related care data will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/children/${id}`);
      toast.success("Child profile deleted");
      fetchChildren();
      refreshUser();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (child) => {
    setEditingChild(child);
    setForm({
      name: child.name,
      date_of_birth: child.date_of_birth,
      conditions: child.conditions,
      notes: child.notes
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({ name: "", date_of_birth: "", conditions: [], notes: "" });
    setEditingChild(null);
    setDialogOpen(false);
  };

  const toggleCondition = (condition) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter(c => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const getAge = (dob) => {
    try {
      return differenceInYears(new Date(), parseISO(dob));
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="children-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Users className="h-8 w-8" />
            Children
          </h1>
          <p className="text-muted-foreground mt-1">Manage your children's profiles</p>
        </div>

        <div className="flex items-center gap-3">
          {user?.limits?.max_children && user?.subscription_tier === 'free' && (
            <span
              className={`text-xs font-medium px-3 py-1.5 rounded-full ${
                (user.usage?.children ?? 0) >= user.limits.max_children
                  ? 'bg-coral-500/10 text-coral-500'
                  : 'bg-muted text-muted-foreground'
              }`}
              data-testid="children-usage-badge"
            >
              {user.usage?.children ?? 0} / {user.limits.max_children} on Free
            </span>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full"
                data-testid="add-child-btn"
                disabled={
                  user?.subscription_tier === 'free' &&
                  user?.limits?.max_children &&
                  (user?.usage?.children ?? 0) >= user.limits.max_children
                }
                title={
                  user?.subscription_tier === 'free' && (user?.usage?.children ?? 0) >= (user?.limits?.max_children ?? Infinity)
                    ? 'Free plan limit reached — Premium coming soon'
                    : ''
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Child
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingChild ? "Edit Child Profile" : "Add Child Profile"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Child's name"
                  required
                  className="rounded-xl"
                  data-testid="child-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="child-dob-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Conditions</Label>
                <div className="flex flex-wrap gap-2">
                  {CONDITIONS.map(condition => (
                    <Badge
                      key={condition}
                      variant={form.conditions.includes(condition) ? "default" : "outline"}
                      className={`cursor-pointer rounded-full transition-all ${
                        form.conditions.includes(condition) 
                          ? "bg-primary" 
                          : "hover:bg-primary/10"
                      }`}
                      onClick={() => toggleCondition(condition)}
                    >
                      {condition}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="rounded-xl resize-none"
                  rows={3}
                  data-testid="child-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-child-btn">
                  {editingChild ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Children Grid */}
      {children.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Sparkles className="h-10 w-10 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">No Children Yet</h3>
            <p className="text-muted-foreground mb-6">Add your first child profile to get started</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Add Child
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {children.map((child, i) => (
            <Card 
              key={child.id} 
              className={`border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-${i + 1}`}
              data-testid={`child-card-${child.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center">
                    <span className="font-heading font-bold text-2xl text-primary">
                      {child.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openEdit(child)}
                      className="rounded-full h-9 w-9"
                      data-testid={`edit-child-${child.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(child.id)}
                      className="rounded-full h-9 w-9 text-destructive hover:text-destructive"
                      data-testid={`delete-child-${child.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <h3 className="font-heading font-bold text-xl mb-1">{child.name}</h3>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Calendar className="h-4 w-4" />
                  {getAge(child.date_of_birth) !== null 
                    ? `${getAge(child.date_of_birth)} years old`
                    : child.date_of_birth
                  }
                </div>

                {child.conditions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {child.conditions.slice(0, 3).map(condition => (
                      <Badge key={condition} variant="secondary" className="rounded-full text-xs">
                        {condition}
                      </Badge>
                    ))}
                    {child.conditions.length > 3 && (
                      <Badge variant="secondary" className="rounded-full text-xs">
                        +{child.conditions.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {child.notes && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{child.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
